/**
 * Admin Custom Output Tables API client — /api/admin-custom-tables.php.
 * Credentialed (admin session). Backs the super-admin scan-sheet designer.
 */

import { CustomTableDef } from '../types';

const API_BASE = window.location.hostname === 'localhost' ? 'http://chriscme/api' : '/api';

export interface AdminCustomTable extends CustomTableDef {
  retailerId: number | null;
}

export interface CustomTableRetailerSummary {
  id: number;
  name: string;
  isPartner: boolean;
}

export interface CustomTablesListResponse {
  tables: AdminCustomTable[];
  retailers: CustomTableRetailerSummary[];
}

/** Writable payload (retailerId null = universal). */
export interface CustomTablePayload {
  retailerId: number | null;
  key: string;
  title: string;
  placement: CustomTableDef['placement'];
  dataSource: CustomTableDef['dataSource'];
  cardsPerRow: number;
  card: CustomTableDef['card'];
  staticCards?: CustomTableDef['staticCards'];
  visibleBrowser: boolean;
  visiblePrint: boolean;
  visiblePdf: boolean;
  isActive?: boolean;
  sortOrder?: number;
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export async function fetchCustomTables(): Promise<CustomTablesListResponse> {
  const res = await fetch(`${API_BASE}/admin-custom-tables.php`, { credentials: 'include' });
  return handle<CustomTablesListResponse>(res);
}

export async function createCustomTable(table: CustomTablePayload): Promise<{ table: AdminCustomTable }> {
  const res = await fetch(`${API_BASE}/admin-custom-tables.php`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ table }),
  });
  return handle(res);
}

export async function updateCustomTable(id: number, table: CustomTablePayload): Promise<{ table: AdminCustomTable }> {
  const res = await fetch(`${API_BASE}/admin-custom-tables.php?id=${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ table }),
  });
  return handle(res);
}

export async function deleteCustomTable(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/admin-custom-tables.php?id=${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Delete failed (${res.status})`);
  }
}
